import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createBooking,
  getMyAddresses,
  saveAddress,
  SavedAddress,
} from "../api";

interface Expert {
  proId: string;
  name: string;
  businessName?: string;
  serviceCategory: string;
  subcategories?: string[];
  hourlyRate: number;
}

// Category-to-pricing-category aliases.
// Pricing table uses short names ("Cleaning") but pro records carry longer
// names ("Cleaning Services"). Keep this map in sync with createPaymentIntent.js.
const CATEGORY_ALIASES: Record<string, string> = {
  "Moving/Storage":         "Moving",
  "Cleaning Services":      "Cleaning",
  "Electrical/Computers":   "Electrical",
  "Painting/Staining":      "Painting",
  "Landscape, Yard/Garden": "Outdoor",
  "Decorator/Designer":     "Interior Design",
};

// jobSize options per pricing category. Values must match the jobSize column
// in mouna-lead-pricing; if absent, createPaymentIntent falls back to mid-tier.
const JOBSIZE_OPTIONS: Record<string, string[]> = {
  Cleaning:          ["Studio/1 Bedroom", "2 Bedrooms", "3 Bedrooms", "4+ Bedrooms"],
  Moving:            ["Studio/1 Bedroom", "2 Bedrooms", "3 Bedrooms", "4+ Bedrooms"],
  Painting:          ["1 Room", "2-3 Rooms", "Whole Home"],
  "Interior Design": ["1 Room", "2-3 Rooms", "Whole Home"],
  Carpentry:         ["Small", "Medium", "Large"],
  Electrical:        ["Small", "Medium", "Large"],
  Outdoor:           ["Small", "Medium", "Large"],
  Plumbing:          ["Small", "Medium", "Large"],
};

export interface BookingPrefill {
  serviceType?: string;
  notes?: string;
  category?: string;
  prefillAddress?: string;
}

interface Props {
  expert: Expert;
  onClose: () => void;
  onBooked: (bookingId: string) => void;
  prefill?: BookingPrefill;
}

export default function BookingModal({ expert, onClose, onBooked, prefill }: Props) {
  const navigate = useNavigate();
  const [serviceType, setServiceType] = useState(prefill?.serviceType || "");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [notes, setNotes] = useState(prefill?.notes || "");
  const [jobSize, setJobSize] = useState("");

  const pricingCategory = CATEGORY_ALIASES[expert.serviceCategory] || expert.serviceCategory;
  const jobSizeOptions = JOBSIZE_OPTIONS[pricingCategory] || ["Small", "Medium", "Large"];

  const [addresses, setAddresses] = useState<SavedAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [useNewAddress, setUseNewAddress] = useState(false);
  const [newAddressText, setNewAddressText] = useState(prefill?.prefillAddress || "");
  const [saveNewAddress, setSaveNewAddress] = useState(false);

  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getMyAddresses()
      .then((list) => {
        setAddresses(list);
        const defaultAddr = list.find((a) => a.isDefault) || list[0];
        if (defaultAddr && !prefill?.prefillAddress) {
          setSelectedAddressId(defaultAddr.addressId);
        } else if (prefill?.prefillAddress) {
          const matched = list.find(
            (a) =>
              a.street.toLowerCase().trim() ===
              prefill.prefillAddress?.split(",")[0].toLowerCase().trim()
          );
          if (matched) {
            setSelectedAddressId(matched.addressId);
          } else {
            setUseNewAddress(true);
          }
        } else if (list.length === 0) {
          setUseNewAddress(true);
        }
      })
      .catch(() => setUseNewAddress(true))
      .finally(() => setLoadingAddresses(false));
  }, [prefill?.prefillAddress]);

  function getSubmitAddress(): string {
    if (useNewAddress) return newAddressText.trim();
    const addr = addresses.find((a) => a.addressId === selectedAddressId);
    if (!addr) return "";
    return `${addr.street}, ${addr.city}${addr.state ? ", " + addr.state : ""} ${addr.zip}`.trim();
  }

  async function handleSubmit() {
    setError("");
    const address = getSubmitAddress();
    if (!serviceType) { setError("Please choose a service."); return; }
    if (!jobSize) { setError("Please pick a job size."); return; }
    if (!scheduledDate) { setError("Please pick a date."); return; }
    if (!address) { setError("Please pick or enter an address."); return; }

    const isoDate = scheduledTime
      ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
      : new Date(`${scheduledDate}T09:00`).toISOString();

    setSubmitting(true);
    try {
      if (useNewAddress && saveNewAddress && newAddressText.trim()) {
        try {
          const parts = newAddressText.split(",").map((s) => s.trim());
          const street = parts[0] || newAddressText.trim();
          const city = parts[1] || "Houston";
          const stateZip = (parts[2] || "TX").trim().split(/\s+/);
          const state = stateZip[0] || "TX";
          const zipMatch = newAddressText.match(/\b\d{5}\b/);
          const zip = zipMatch ? zipMatch[0] : "";
          if (zip) {
            await saveAddress({
              label: "Booking",
              street, city, state, zip,
              notes: "",
              isDefault: addresses.length === 0,
            });
          }
        } catch (saveErr) {
          console.warn("Could not save address:", saveErr);
        }
      }

      const booking = await createBooking({
        proId: expert.proId,
        serviceType,
        scheduledDate: isoDate,
        address,
        notes,
        category: expert.serviceCategory,
        jobSize,
      });

      onBooked(booking.bookingId || "");
      navigate("/bookings/confirmation", {
        state: {
          booking: {
            ...booking,
            proName: booking.proName || expert.businessName || expert.name,
            proCategory: booking.proCategory || expert.serviceCategory,
          },
        },
      });
    } catch (err: any) {
      console.error("Booking error:", err);
      setError(err.response?.data?.error || "Couldn't create booking. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const today = new Date().toISOString().split("T")[0];
  const subcategoryOptions =
    expert.subcategories && expert.subcategories.length > 0
      ? expert.subcategories
      : ["General service"];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-xl font-bold text-navy truncate">
              {success ? "Booked!" : `Book ${expert.businessName || expert.name}`}
            </h2>
            {!success && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {expert.serviceCategory}
                {expert.hourlyRate > 0 ? ` · $${expert.hourlyRate}/hr` : ""}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-bold hover:bg-gray-200 ml-3"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {success ? (
            <div className="text-center py-8">
              <div className="text-5xl mb-3">✅</div>
              <p className="font-bold text-navy mb-1">Request sent.</p>
              <p className="text-sm text-gray-500">
                {expert.businessName || expert.name} will respond shortly.
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-4 text-sm text-red-800">
                  {error}
                </div>
              )}

              <Field label="Service *">
                <select
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm bg-white"
                >
                  <option value="">Choose a service…</option>
                  {subcategoryOptions.map((sub) => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Job size *">
                <div className="flex flex-wrap gap-2">
                  {jobSizeOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setJobSize(opt)}
                      className={
                        "px-4 py-2 rounded-full text-sm font-bold border transition-colors " +
                        (jobSize === opt
                          ? "bg-mouna-blue text-white border-mouna-blue"
                          : "bg-white text-navy border-gray-300 hover:border-mouna-blue")
                      }
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Date *">
                  <input
                    type="date"
                    min={today}
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm"
                  />
                </Field>
                <Field label="Time">
                  <input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm"
                  />
                </Field>
              </div>

              <Field label="Address *">
                {loadingAddresses ? (
                  <div className="text-xs text-gray-400 py-2">Loading saved addresses…</div>
                ) : (
                  <>
                    {!useNewAddress && addresses.length > 0 && (
                      <>
                        <div className="space-y-2 mb-2">
                          {addresses.map((a) => (
                            <button
                              key={a.addressId}
                              type="button"
                              onClick={() => setSelectedAddressId(a.addressId)}
                              className={
                                "w-full text-left px-3 py-2 rounded-xl border-2 transition-colors " +
                                (selectedAddressId === a.addressId
                                  ? "border-mouna-blue bg-blue-50"
                                  : "border-gray-200 hover:border-gray-300")
                              }
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-navy">
                                  {a.label || "Saved"}
                                </span>
                                {a.isDefault && (
                                  <span className="text-xs bg-blue-100 text-mouna-blue px-1.5 py-0.5 rounded font-bold">
                                    Default
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5">
                                {a.street}, {a.city}{a.state && `, ${a.state}`} {a.zip}
                              </div>
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => { setUseNewAddress(true); setSelectedAddressId(""); }}
                          className="text-xs text-mouna-blue font-bold hover:underline"
                        >
                          + Use a different address
                        </button>
                      </>
                    )}

                    {(useNewAddress || addresses.length === 0) && (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={newAddressText}
                          onChange={(e) => setNewAddressText(e.target.value)}
                          placeholder="123 Main St, Houston, TX 77001"
                          className="w-full px-3 py-2.5 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm"
                        />
                        <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={saveNewAddress}
                            onChange={(e) => setSaveNewAddress(e.target.checked)}
                          />
                          Save this address for next time
                        </label>
                        {addresses.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setUseNewAddress(false);
                              const def = addresses.find((a) => a.isDefault) || addresses[0];
                              setSelectedAddressId(def.addressId);
                            }}
                            className="text-xs text-mouna-blue font-bold hover:underline"
                          >
                            ← Pick from saved addresses
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Field>

              <Field label="Details for the pro (optional)">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="What should they know? Gate code, parking, what needs doing…"
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-mouna-blue focus:outline-none text-sm resize-none"
                />
              </Field>
            </>
          )}
        </div>

        {!success && (
          <div className="border-t border-gray-100 p-4 flex gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-3 rounded-full bg-gray-100 text-navy font-bold text-sm hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-[2] py-3 rounded-full bg-mouna-blue text-white font-bold text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Send booking request"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <div className="text-xs font-bold text-gray-600 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
